<template>
  <div class="test-step">
    <!-- Active: SMS is live -->
    <collapsible-section v-if="state === 'active'" :collapsible="false" title="SMS is now active" icon="tim-icons icon-check-2">
      <p class="test-step__lead" v-if="isCancelled">Text messaging is cancelled for this location. The number below was released and the subscription ends with the current billing cycle.</p>
      <p class="test-step__lead" v-else-if="isPaused">Text messaging is paused for this location. Your number is kept, nothing is sent or received, and the subscription ends with the current billing cycle. A number paused for more than 90 days may be reclaimed by the carrier.</p>
      <p class="test-step__lead" v-else>Text messaging is live for this location. Automations can now send texts, and every message is logged with its delivery status.</p>
      <p class="test-step__hint" v-if="settings && settings.display_phone_number">{{ isCancelled ? 'Released number: ' : isPaused ? 'Paused number: ' : 'Your number: ' }}{{ settings.display_phone_number }}</p>
      <base-button v-if="isPaused" type="primary" size="sm" @click="$emit('resume-sms')">
        <i class="tim-icons icon-triangle-right-17 mr-1"></i> Resume SMS
      </base-button>

      <!-- Joined field + button, same amount-input-group primitive the area
           code search uses on the number step. -->
      <label v-if="isLive">Send another test to</label>
      <div v-if="isLive" class="amount-input-group test-step__send-group">
        <base-input class="amount-input-wrapper mb-0">
          <phone-number-input
            v-model="localData.test_phone"
            default-country="US"
          />
        </base-input>
        <base-button
          type="primary"
          class="test-step__send-btn"
          :disabled="sendingTest || !localData.test_phone"
          @click="$emit('send-test', { test_phone: localData.test_phone })"
        >
          {{ sendingTest ? 'Sending...' : 'Send test' }}
        </base-button>
      </div>

      <!-- Reports what the carrier actually did, not merely that Twilio
           accepted the message. "Queued" is not success. -->
      <info-hint v-if="testResult" :icon="testResultIcon" :variant="testResultVariant">
        {{ testResultText }}
      </info-hint>

      <div v-if="isLive" class="danger-action-card mt-4">
        <div class="danger-action-info">
          <span class="danger-action-label text-white">Pause SMS</span>
          <span class="danger-action-hint">Keeps your number but stops all texting until you resume. Nothing is sent or received while paused, so there are no message charges, and the monthly subscription stops after the current billing cycle. Pausing for more than 90 days risks losing the number.</span>
        </div>
        <base-button size="sm" class="danger-action-btn" @click="confirmPause.show = true">
          <i class="tim-icons icon-button-pause mr-1" style="font-size: 11px;"></i> Pause SMS
        </base-button>
      </div>
      <base-alert-dialog
        :show="confirmPause.show"
        title="Pause SMS?"
        message="Texting stops right away and automations skip their texts until you resume. Your number and registration are kept. The subscription ends with the current cycle and restarts in full when you resume."
        variant="warning"
        show-cancel
        confirm-text="Pause SMS"
        cancel-text="Keep sending"
        @confirm="onConfirmPause"
        @cancel="confirmPause.show = false"
      />
      <div v-if="isLive" class="danger-action-card mt-3">
        <div class="danger-action-info">
          <span class="danger-action-label text-white">Cancel SMS service</span>
          <span class="danger-action-hint">Releases your phone number and stops the monthly subscription after the current billing cycle. Messages already sent this cycle still count, nothing is refunded, and the number cannot be recovered.</span>
        </div>
        <base-button size="sm" class="danger-action-btn" @click="confirmCancel.show = true">
          <i class="tim-icons icon-simple-remove mr-1" style="font-size: 11px;"></i> Cancel SMS
        </base-button>
      </div>
      <div v-if="isLive" class="danger-action-card mt-3">
        <div class="danger-action-info">
          <span class="danger-action-label text-white">Replace phone number</span>
          <span class="danger-action-hint">Releases your current number and lets you pick a new one. Use this if the number is receiving spam. Texts stop until the new number is activated, customers who saved the old number can no longer reach you, and the subscription continues unchanged.</span>
        </div>
        <base-button size="sm" class="danger-action-btn" @click="confirmReplace.show = true">
          <i class="tim-icons icon-refresh-02 mr-1" style="font-size: 11px;"></i> Replace number
        </base-button>
      </div>
      <base-alert-dialog
        :show="confirmReplace.show"
        title="Replace your phone number?"
        message="Your current number is released right away and cannot be recovered. Text messaging pauses until you choose and activate a new number, then automations resume on their own. Your registration carries over, so there is no new activation fee."
        variant="danger"
        show-cancel
        confirm-text="Replace number"
        cancel-text="Keep number"
        confirm-variant="danger"
        @confirm="onConfirmReplace"
        @cancel="confirmReplace.show = false"
      />
      <base-alert-dialog
        :show="confirmCancel.show"
        title="Cancel SMS service?"
        message="Your number will be released and text messaging turned off for this location. The current cycle's subscription still bills and nothing is refunded. Re-enabling later requires contacting support."
        variant="danger"
        show-cancel
        confirm-text="Cancel SMS"
        cancel-text="Keep SMS"
        confirm-variant="danger"
        @confirm="onConfirmCancel"
        @cancel="confirmCancel.show = false"
      />
    </collapsible-section>

    <!-- Ready: send the test -->
    <collapsible-section v-else :collapsible="false" title="Send yourself a test" icon="tim-icons icon-send" description="One real text from your new number confirms everything is wired up. Sending it activates SMS for this location.">
      <div class="row">
        <div class="col-lg-5">
          <base-input label="Send test to">
            <phone-number-input
              v-model="localData.test_phone"
              default-country="US"
            />
          </base-input>
          <info-hint icon="icon-bulb-63" variant="neutral">
            Pre-filled from your staff profile when available. This test uses one SMS credit. Activating starts your $10 monthly subscription for this number.
          </info-hint>
        </div>
      </div>
    </collapsible-section>
  </div>
</template>

<script>
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import InfoHint from '@/components/InfoHint.vue';
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection';

export default {
  name: 'SMSOnboardingStepTest',
  components: {
    PhoneNumberInput,
    InfoHint,
    CollapsibleSection
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      default: null
    },
    testResult: {
      type: Object,
      default: null
    },
    sendingTest: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // Prefill from the logged-in user's staff profile (localStorage.profile
    // carries the MyProfile user object incl. phone; verified 2026-07-18).
    let profilePhone = '';
    try {
      const profile = JSON.parse(localStorage.getItem('profile') || 'null');
      profilePhone = profile?.phone || '';
    } catch (e) {
      profilePhone = '';
    }
    return {
      localData: {
        test_phone: this.formData?.test_phone || profilePhone
      },
      confirmCancel: { show: false },
      confirmReplace: { show: false },
      confirmPause: { show: false }
    };
  },
  computed: {
    state() {
      const status = this.settings?.registration_status || 'not_started';
      return ['active', 'suspended', 'cancelled', 'paused'].includes(status) ? 'active' : 'ready';
    },
    isCancelled() {
      return this.settings?.registration_status === 'cancelled';
    },
    isPaused() {
      return this.settings?.registration_status === 'paused';
    },
    // Test sends and the three service cards only make sense while service is on
    isLive() {
      return !this.isCancelled && !this.isPaused;
    },
    testResultVariant() {
      if (!this.testResult) return 'neutral';
      if (this.testResult.delivered) return 'success';
      return this.testResult.settled ? 'warning' : 'neutral';
    },
    testResultIcon() {
      if (this.testResult && this.testResult.delivered) return 'icon-check-2';
      return this.testResult && this.testResult.settled ? 'icon-alert-circle-exc' : 'icon-time-alarm';
    },
    testResultText() {
      const r = this.testResult;
      if (!r) return '';
      if (r.delivered) return 'Delivered. Your phone should have the message.';
      if (r.settled) {
        const code = r.error_code ? ` (carrier code ${r.error_code})` : '';
        const detail = r.error_message ? ` ${r.error_message}` : '';
        return `The carrier did not deliver this message${code}.${detail} A number added to a campaign in the last few hours often needs time before carriers accept its traffic, so this is worth retrying later.`;
      }
      return `Twilio accepted the message and it is still ${r.status}. That is not the same as delivered. Check your phone, and send another test in a few minutes if nothing arrives.`;
    }
  },
  methods: {
    // Hide the dialog first and let it restore page scroll before the parent
    // acts, the same sequencing every base-alert-dialog over a drawer uses.
    async onConfirmCancel() {
      this.confirmCancel.show = false;
      await this.$nextTick();
      this.$emit('release-number');
    },
    async onConfirmReplace() {
      this.confirmReplace.show = false;
      await this.$nextTick();
      this.$emit('replace-number');
    },
    async onConfirmPause() {
      this.confirmPause.show = false;
      await this.$nextTick();
      this.$emit('pause-sms');
    }
  },
  watch: {
    localData: {
      deep: true,
      handler(newVal) {
        this.$emit('update', { ...newVal });
      }
    }
  },
  mounted() {
    // The prefill must reach the wizard's formData even if the user never
    // touches the field, so Continue submits the prefilled value.
    if (this.localData.test_phone) {
      this.$emit('update', { ...this.localData });
    }
  }
};
</script>

<style lang="scss" scoped>
// Standard amount-input-group block. Per-consumer by convention (reference
// implementation TransactionDrawer.vue), so it has to be repeated here rather
// than inherited: without it the class is inert and the button drops below the
// field instead of sitting flush to its right.
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  &:focus-within { border-color: #e14eca; }
}
.amount-input-wrapper {
  flex: 1;
  ::v-deep .form-control {
    border: none;
    border-radius: 0;
    background: transparent;
  }
}

.test-step {
  // Mirrors number-step__search-group / __search-btn so the joined field and
  // button read identically on both steps. Wider than the area code group
  // because a formatted phone number plus the flag needs the room.
  &__send-group {
    max-width: 420px;
  }

  &__send-btn {
    margin: 0;
    border-radius: 0;
    flex-shrink: 0;
  }

  &__lead {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    line-height: 1.55;
    margin-bottom: 0.75rem;
  }

  &__hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.75rem;
    line-height: 1.5;
    margin-top: 0.25rem;
    margin-bottom: 0.75rem;
  }
}
</style>

<style>
.white-content .test-step__lead { color: rgba(0, 0, 0, 0.8); }
.white-content .test-step__hint { color: rgba(0, 0, 0, 0.6); }
</style>
